import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ChatHoursModalWindowComponent } from 'src/app/shared/components/chat-hours-modal-window/chat-hours-modal-window.component';
import { DeviceSelectionDialogComponent } from 'src/app/shared/components/device-selection-dialog/device-selection-dialog.component';
import { ADPResourceService } from 'digital-core';
import { UtilityService } from '../services/utility.service';
import { ActivatedRoute } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { AsappChatService } from '../services/asapp-chat.service';
import { MiawChatService } from '../services/miaw-chat.service';
import { Subscription } from 'rxjs';
import { ProgressLoaderAssurantService } from '../services/progress-loader-assurant.service';

@Injectable({
  providedIn: 'root',
})
export class ChatHandler implements OnDestroy {
  public isNative: boolean;
  public chatOffHoursStartTimeInUTC = '4';
  public chatOffHoursEndTimeInUTC = '12';
  private queryParamsSubscription: Subscription | null = null;
  private chatMaximizedHandler: (() => void) | null = null;

  constructor(
    private matDialog: MatDialog,
    public resourceService: ADPResourceService,
    public utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private asappChatService: AsappChatService,
    private miawChatService: MiawChatService,
    private progressLoaderService: ProgressLoaderAssurantService,
  ) {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.isNative = (params.isNative && params.isNative.toLowerCase().trim() === 'true') || this.utilityService.isNativeApp;
    });
  }
  ngOnDestroy(): void {
    // Unsubscribe from query params
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
      this.queryParamsSubscription = null;
    }
    // Remove chat maximized event listener
    this.removeChatMaximizedListener();
  }

  private removeChatMaximizedListener(): void {
    if (this.chatMaximizedHandler) {
      window.removeEventListener('onEmbeddedMessagingWindowMaximized', this.chatMaximizedHandler);
      this.chatMaximizedHandler = null;
    }
  }

  public openDeviceSelectionDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = 'device-selection-dialog';

    const dialogRef = this.matDialog.open(DeviceSelectionDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'phone') {
        // Open Asapp chat
        this.openAsappChat();
      } else if (result === 'home') {
        // Trigger checkChatHours function
        this.openTechSupportChat();
      }
    });
  }

  public openTechSupportChat() {
    // Don't attempt to launch chat in native mobile app
    if (this.isNative) {
      return;
    }

    if(this.miawChatService.isInitialized) {
      this.hideAsappChat();

      // Show loader while chat is opening
      this.progressLoaderService.show();

      // Remove any existing listener before adding a new one
      this.removeChatMaximizedListener();

      // Hide loader when chat window is maximized
      this.chatMaximizedHandler = () => {
        this.progressLoaderService.hide();
        this.removeChatMaximizedListener();
      };
      window.addEventListener('onEmbeddedMessagingWindowMaximized', this.chatMaximizedHandler, { once: true });

      this.miawChatService.show();
      return;
    } else {
      this.openUnavailableDialog();
    }
  }

  openChatInterface(hasCoverageSupport = false, hasTechServices = true) {
    // Don't attempt to launch chat in native mobile app
    if (this.isNative) {
      return;
    }

    // User has both mobile and home tech policies
    if (hasCoverageSupport && hasTechServices) {
      this.openDeviceSelectionDialog();
      return;
    }

    // User has only mobile policy
    if (hasCoverageSupport) {
      this.openAsappChat();
      return;
    }

    // User has only home tech policy
    if (hasTechServices) {
      this.openTechSupportChat();
      return;
    }

    // If no policies are selected, no action required
  }

  openAsappChat() {
    // Don't attempt to launch chat in native mobile app
    if (this.isNative) {
      return;
    }
    if (this.asappChatService.isInitialized) {
      this.hideTechSupportChat();
      this.asappChatService.show();
    } else {
      this.openUnavailableDialog();
    }
  }

  hideAsappChat() {
    if (this.asappChatService.isInitialized) {
      this.asappChatService.hide();
    }
  }

  hideTechSupportChat() {
    if (this.miawChatService.isInitialized) {
      this.miawChatService.hide();
    }
  }

  openUnavailableDialog() {
    this.chatOffHoursStartTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_StartTime_UTC24HT')?.Value ?? '4';
    this.chatOffHoursEndTimeInUTC = this.resourceService.getPortalSetting('Chat_OffHours_EndTime_UTC24HT')?.Value ?? '12';

    const dialogConfig = new MatDialogConfig();
    const now = new Date();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'chat-hours-dialog';
    dialogConfig.height = 'auto';
    dialogConfig.data =
      now.getUTCHours() >= parseInt(this.chatOffHoursStartTimeInUTC) && now.getUTCHours() < parseInt(this.chatOffHoursEndTimeInUTC)
        ? {
          title: 'Chat_Unavailable_Modal_Title',
          content: 'Chat_Unavailable_Modal_Content',
          buttonText: 'Global_Button_Close',
        }
        : {
          title: 'Chat_NoAgents_Modal_Title',
          content: 'Chat_NoAgents_Modal_Content',
          buttonText: 'Global_Button_Close',
        };

    this.matDialog.open(ChatHoursModalWindowComponent, dialogConfig);
  }
}
